import React, { useState, useEffect, useContext } from "react";
import { Formik, Form } from "formik";
import {
  Box,
  Button,
  Flex,
  FormLabel,
  Grid,
  HStack,
  Icon,
  useToast,
} from "@chakra-ui/react";
import * as Yup from "yup";
import CustomField from "../../Common/Form/CustomField";
import { useHistory } from "react-router-dom";
import { arrayToOption } from "../../../Utils/formatUtils";
import { UsersContext } from "../../Context/UsersContext";
import useFetch from "../../../hooks/useFetch";
import ClientsTable from "./Seller/ClientsTable";
import ProductList from "./Client/ProductList";
import { Prompt } from "react-router";
import ConfirmationSwal from "../../../Utils/confirmationSwal";
import { BiCopyAlt } from "react-icons/bi";

const UserForm = ({ user }) => {
  const { getZones, getPriceLists } = useContext(UsersContext);
  const [newPass, setNewPass] = useState(null);
  const [zonesOptions, setZonesOptions] = useState([]);
  const [priceListsOptions, setPriceListsOptions] = useState([]);
  const [sellerListOptions, setSellerListsOptions] = useState([]);
  const [rolesOptions, setRolesOptions] = useState([]);
  const [validation, setValidation] = useState({});
  const [newRole, setNewRole] = useState(user.roleId);
  const [newClients, setNewClients] = useState(user.clients);
  const [customPriceList, setCustomPriceList] = useState([]);
  const [isSubmitting, setIsSubmiting] = useState(false);
  const [isEdited, setIsEdited] = useState(false);
  const [prevLocation, setPrevLocation] = useState("/clientes");
  const history = useHistory();
  const toast = useToast();
  const { get, put, post } = useFetch();

  const setInitialData = async () => {
    setRolesOptions(arrayToOption(await get("/users/roles")));
    setZonesOptions(arrayToOption(await getZones()));
    setPriceListsOptions(arrayToOption(await getPriceLists()));
    const sellerList = await get("/users?roleId=3");
    setSellerListsOptions(
      arrayToOption(sellerList?.users, "id", "businessName")
    );
  };

  useEffect(() => {
    if (user.roleId == 3) {
      setPrevLocation("/vendedores");
    } else if (user.roleId == 2) {
      setPrevLocation("/operadores");
    }
    setInitialData();
  }, []);

  useEffect(() => {
    setValidation(setInitialSchema());
  }, [newRole]);

  const setInitialSchema = () => {
    let validationShape = Yup.object().shape({
      businessName: Yup.string().required("Ingrese la razón social"),
      email: Yup.string().required("Ingrese un email"),
      roleId: Yup.object().nullable().required("Ingrese un tipo de usuario"),
    });
    if (!user.id)
      validationShape = validationShape.shape({
        pass: Yup.string()
          .min(8, "La contraseña debe ser de por lo menos 8 caracteres")
          .required("Ingrese una contraseña"),
      });

    if (newRole == 4) {
      validationShape = validationShape.shape({
        zoneId: Yup.object().nullable().required("Ingrese una zona"),
        priceListId: Yup.object()
          .nullable()
          .required("Elija una lista de precios"),
      });
    }

    return validationShape;
  };

  const submitHandler = async (form, isRedirecting) => {
    await form.submitForm();
    if (Object.keys(await form.validateForm()).length !== 0) return false;
    setIsSubmiting(true);
    const value = form.values;
    let body = {
      businessName: value.businessName,
      firstName: value.firstName,
      lastName: value.lastName,
      email: value.email,
      phone: value.phone,
      pass: value.pass,
      roleId: value.roleId.value,
      sellerId: value?.sellerId?.value,
      address: value.address,
      cuit: value.cuit,
      active: value.active ? 1 : 0,
      zoneId:
        value.roleId.value == 4 && value.zoneId.value
          ? value.zoneId.value
          : null,
      priceListId:
        value.roleId.value == 4 && value.priceListId.value
          ? value.priceListId.value
          : null,
    };

    let title = "Usuario creado con éxito.";
    let description = `Se creó al ${value.roleId.label} correctamente.`;
    setIsEdited(false);
    try {
      let newId = 0;
      if (!user.id) {
        await post("/users", body).then((res) => {
          newId = res.id;
        });
      } else {
        await put(`/users/${user.id}`, body);
        newClients &&
          (await put(`/users/${user.id}/clients`, { clients: newClients }));
        if (customPriceList.length) {
          await put(`/users/${user.id}/price-lists`, {
            priceList: customPriceList,
          });
        }
        title = "Usuario editado con éxito.";
        description = "Se editó el usuario correctamente.";
      }
      toast({
        title,
        description,
        status: "success",
        duration: 4000,
        isClosable: true,
      });

      if (isRedirecting) {
        history.push(prevLocation);
      } else if (!user.id) {
        history.replace(prevLocation);
        history.push(`/usuarios/${newId}`);
      }
    } catch (e) {
      toast({
        title: "Se ha producido un error.",
        description: e,
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setIsSubmiting(false);
    }
  };

  const setInitialValues = () => {
    const rol = rolesOptions.find((role) => role.value == user.roleId);
    const zone = zonesOptions.find((zone) => zone.value == user.zoneId);
    const priceList = priceListsOptions.find(
      (priceList) => priceList.value == user.priceListId
    );
    const seller = sellerListOptions.find(
      (seller) => seller.value == user?.seller?.[0]?.id
    );
    if (!user.id) {
      return {
        active: 1,
        businessName: "",
        email: "",
        phone: "+54911",
        pass: "",
        roleId: rol,
        zoneId: null,
        priceListId: null,
        sellerId: null,
        firstName: "",
        lastName: "",
        address: "",
        cuit: "",
      };
    }
    let parsedUser = { ...user };
    parsedUser.roleId = rol;
    parsedUser.zoneId = zone;
    parsedUser.priceListId = priceList;
    parsedUser.sellerId = seller;
    return parsedUser;
  };

  const handleIsEdited = () => {
    setIsEdited(true);
  };

  const handleReset = async () => {
    const generateRandomString = (length) => {
      const characters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      return Array.from({ length }, () =>
        characters.charAt(Math.floor(Math.random() * characters.length))
      ).join("");
    };

    const pass = generateRandomString(10);
    await post("/session/reset-pass", {
      email: user.email,
      pass: pass,
      code: process.env.REACT_APP_MASTER_RESET_CODE,
    })
      .then((res) => {
        toast({
          title: "Se ha reseteado la contraseña correctamente.",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        setNewPass(pass);
      })
      .catch((e) => {
        toast({
          title: "Se ha producido un error.",
          description: e,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      });
  };

  const handleCopyClick = () => {
    const tempElement = document.createElement("textarea");
    tempElement.value = newPass;
    document.body.appendChild(tempElement);
    tempElement.select();
    document.execCommand("copy");
    document.body.removeChild(tempElement);
    toast({
      title: `Nueva contraseña ${newPass} copiada.`,
      status: "success",
      duration: 4000,
      isClosable: true,
    });
  };

  const handleDelete = () => {
    ConfirmationSwal.fire({
      title: "Eliminar usuario",
      text: "¿Está seguro que desea eliminar el usuario? Este proceso es irreversible.",
      icon: "question",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await put(`/users/${user.id}`, { deleted: 1 })
          .then((res) => {
            toast({
              title: `Usuario eliminado con éxito.`,
              status: "success",
              duration: 4000,
              isClosable: true,
            });
            history.push(prevLocation);
          })
          .catch((e) => {
            toast({
              title: "Se ha producido un error.",
              description:
                e?.message || "Contacte con el administrador del sistema.",
              status: "error",
              duration: 4000,
              isClosable: true,
            });
          });
      }
    });
  };

  const handleBlockedNavigation = (nextLocation) => {
    if (isEdited) {
      setIsEdited(false);
      ConfirmationSwal.fire({
        title: "Parece que hay cambios no guardados",
        text: "Desea salir de la página?",
        icon: "question",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "No",
      }).then(async (result) => {
        if (result.isConfirmed) {
          history.push(nextLocation.pathname);
        }
      });
      return false;
    }
    return true;
  };

  return (
    <>
      <Box pb="1rem">
        <Formik
          initialValues={setInitialValues()}
          validationSchema={validation}
          onSubmit={() => {}}
          enableReinitialize={true}
        >
          {(props) => (
            <Form>
              <Grid
                templateColumns={["repeat(1, 1fr)", "repeat(3, 1fr)"]}
                gap={6}
              >
                <CustomField
                  name="roleId"
                  title="Tipo de usuario"
                  placeholder="Elija un tipo"
                  inputType="select"
                  props={props}
                  options={rolesOptions}
                  customStyle={{ textTransform: "uppercase" }}
                  onChangeFunc={(value) => setNewRole(value)}
                  setIsEdited={handleIsEdited}
                ></CustomField>
                <CustomField
                  name="businessName"
                  title="Razón Social"
                  placeholder="Ingrese un nombre"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>

                <CustomField
                  name="email"
                  title="Email"
                  placeholder="Ingrese un email"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>
                {user.id ? (
                  <>
                    <Flex
                      direction={"column"}
                      w="100%"
                      display="flex"
                      h={"auto"}
                      alignItems={"auto"}
                    >
                      <FormLabel
                        mb="0"
                        style={{ color: "menu.700" }}
                        fontSize={"0.9rem"}
                      >
                        CONTRASEÑA
                      </FormLabel>
                      <HStack>
                        <Button
                          colorScheme="blue"
                          onClick={() => {
                            handleReset();
                          }}
                          w="60%"
                        >
                          Resetear
                        </Button>
                        <Button
                          colorScheme="blue"
                          onClick={() => {
                            handleCopyClick();
                          }}
                          w="35%"
                          disabled={!newPass}
                        >
                          Copiar <Icon as={BiCopyAlt}></Icon>
                        </Button>
                      </HStack>
                    </Flex>
                  </>
                ) : (
                  <CustomField
                    name="pass"
                    title="Contraseña"
                    placeholder="Ingrese una contraseña"
                    props={props}
                    customStyle={{ textTransform: "uppercase" }}
                    setIsEdited={handleIsEdited}
                  ></CustomField>
                )}

                <CustomField
                  name="firstName"
                  title="Nombre"
                  placeholder="Ingrese un nombre"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>

                <CustomField
                  name="lastName"
                  title="Apellido"
                  placeholder="Ingrese un apellido"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>

                <CustomField
                  name="phone"
                  title="Celular"
                  placeholder="Ingrese un celular"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>
                <CustomField
                  name="address"
                  title="Dirección"
                  placeholder="Ingrese una dirección"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>

                <CustomField
                  name="cuit"
                  title="CUIT"
                  placeholder="Ingrese el CUIT"
                  props={props}
                  customStyle={{ textTransform: "uppercase" }}
                  setIsEdited={handleIsEdited}
                ></CustomField>

                {props.values.roleId && props.values.roleId.value == 4 ? (
                  <>
                    <CustomField
                      name="zoneId"
                      title="Zonas"
                      placeholder="Elija una zona"
                      inputType="select"
                      props={props}
                      options={zonesOptions}
                      customStyle={{ textTransform: "uppercase" }}
                      setIsEdited={handleIsEdited}
                    ></CustomField>
                    <CustomField
                      name="priceListId"
                      title="Lista de precios"
                      placeholder="Elija una lista de precios"
                      inputType="select"
                      props={props}
                      options={priceListsOptions}
                      customStyle={{ textTransform: "uppercase" }}
                      setIsEdited={handleIsEdited}
                    ></CustomField>
                    <CustomField
                      name="sellerId"
                      title="Vendedor"
                      placeholder="Elija un vendedor"
                      inputType="select"
                      props={props}
                      options={sellerListOptions}
                      customStyle={{ textTransform: "uppercase" }}
                      setIsEdited={handleIsEdited}
                    ></CustomField>
                  </>
                ) : null}
                <CustomField
                  name="active"
                  title="Activo"
                  hLabel={true}
                  inputType="switch"
                  props={props}
                  customStyle={{
                    textTransform: "uppercase",
                  }}
                  setIsEdited={handleIsEdited}
                ></CustomField>
                {user.id && (
                  <Button
                    colorScheme="red"
                    onClick={() => {
                      handleDelete();
                    }}
                    w="60%"
                  >
                    Eliminar usuario
                  </Button>
                )}
              </Grid>
              {user.id &&
              props.values.roleId &&
              props.values.roleId.value == 4 ? (
                <Box p="2rem 0 ">
                  <ProductList
                    userId={user.id}
                    priceListId={user.priceListId}
                    setCustomPriceList={setCustomPriceList}
                    setIsEdited={handleIsEdited}
                  ></ProductList>
                </Box>
              ) : null}

              {user.id &&
              props.values.roleId &&
              props.values.roleId.value == 3 ? (
                <Box p="2rem 0 ">
                  <ClientsTable
                    newClients={newClients}
                    setNewClients={setNewClients}
                    setIsEdited={handleIsEdited}
                  ></ClientsTable>
                </Box>
              ) : null}

              <HStack
                justify={["center", "flex-end"]}
                mt="2rem"
                wrap={"wrap"}
                gap="0.5rem"
              >
                <Button
                  variant="ghost"
                  onClick={() => {
                    setIsEdited(false);
                    history.goBack();
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  onClick={() => submitHandler(props, true)}
                >
                  Guardar
                </Button>
                <Button
                  colorScheme="blue"
                  isLoading={isSubmitting}
                  onClick={() => submitHandler(props, false)}
                >
                  Guardar y seguir editando
                </Button>
              </HStack>
            </Form>
          )}
        </Formik>
      </Box>
      <Prompt when={isEdited} message={(e) => handleBlockedNavigation(e)} />
    </>
  );
};

export default UserForm;
